/**
 * VP9 Alpha channel helper.
 *
 * Ported from direct_connect.html.
 * - SDP interceptor: reorders VP9 payload types to the front of the m=video line
 * - Native alpha detection: samples corner pixels to determine if VP9 alpha is preserved
 */

// ==================== SDP Interceptor ====================

function preferVP9InSDP(sdp: string): string {
  const lines = sdp.split('\r\n');
  let inVideoSection = false;
  let videoMLineIndex = -1;
  const vp9PayloadTypes: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    if (lines[i].startsWith('m=video')) {
      inVideoSection = true;
      videoMLineIndex = i;
    } else if (lines[i].startsWith('m=') && !lines[i].startsWith('m=video')) {
      inVideoSection = false;
    }
    if (inVideoSection && lines[i].startsWith('a=rtpmap:')) {
      const match = lines[i].match(/^a=rtpmap:(\d+)\s+VP9\/\d+/i);
      if (match) vp9PayloadTypes.push(match[1]);
    }
  }

  if (vp9PayloadTypes.length === 0 || videoMLineIndex === -1) {
    return sdp;
  }

  const mLine = lines[videoMLineIndex];
  const parts = mLine.split(' ');
  const prefix = parts.slice(0, 3);
  const payloads = parts.slice(3);

  const vp9Set = new Set(vp9PayloadTypes);
  const reordered = [
    ...vp9PayloadTypes.filter((pt) => payloads.includes(pt)),
    ...payloads.filter((pt) => !vp9Set.has(pt)),
  ];

  lines[videoMLineIndex] = [...prefix, ...reordered].join(' ');
  console.log(`[VP9 SDP] VP9 payload type(s) [${vp9PayloadTypes.join(', ')}] reordered to front`);
  return lines.join('\r\n');
}

let _vp9Patched = false;

/**
 * Monkey-patch RTCPeerConnection to reorder VP9 to the front of the SDP codec list.
 * Must be called before any LiveKit room.connect().
 * Safe to call multiple times (no-op after first install).
 */
export function installVP9SDPInterceptor(): void {
  if (_vp9Patched) return;
  _vp9Patched = true;

  const origSetLocal = RTCPeerConnection.prototype.setLocalDescription as Function;
  const origSetRemote = RTCPeerConnection.prototype.setRemoteDescription as Function;

  RTCPeerConnection.prototype.setLocalDescription = function (
    this: RTCPeerConnection,
    desc: RTCLocalSessionDescriptionInit,
  ) {
    if (desc && desc.type === 'offer' && desc.sdp) {
      desc = { type: desc.type, sdp: preferVP9InSDP(desc.sdp) };
    }
    return origSetLocal.call(this, desc);
  } as typeof RTCPeerConnection.prototype.setLocalDescription;

  RTCPeerConnection.prototype.setRemoteDescription = function (
    this: RTCPeerConnection,
    desc: RTCSessionDescriptionInit,
  ) {
    if (desc && desc.sdp) {
      desc = { type: desc.type, sdp: preferVP9InSDP(desc.sdp) };
    }
    return origSetRemote.call(this, desc);
  } as typeof RTCPeerConnection.prototype.setRemoteDescription;
}

// ==================== Alpha Detection ====================

/**
 * Sample the alpha channel of the current video frame.
 * Returns true if native VP9 alpha is detected (corner avg alpha <= 64).
 */
export function detectNativeAlpha(video: HTMLVideoElement): boolean {
  try {
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (vw < 8 || vh < 8) return false;

    const probe = document.createElement('canvas');
    const sampleSize = 8;
    probe.width = vw;
    probe.height = vh;
    const pctx = probe.getContext('2d', { willReadFrequently: true });
    if (!pctx) return false;
    pctx.drawImage(video, 0, 0, vw, vh);

    function avgAlphaInRect(x: number, y: number, w: number, h: number): number {
      const imgData = pctx!.getImageData(x, y, w, h).data;
      let total = 0;
      for (let i = 3; i < imgData.length; i += 4) total += imgData[i];
      return total / (imgData.length / 4);
    }

    const topLeft = avgAlphaInRect(0, 0, sampleSize, sampleSize);
    const topRight = avgAlphaInRect(vw - sampleSize, 0, sampleSize, sampleSize);
    const bottomLeft = avgAlphaInRect(0, vh - sampleSize, sampleSize, sampleSize);
    const bottomRight = avgAlphaInRect(vw - sampleSize, vh - sampleSize, sampleSize, sampleSize);
    const cornerAvg = (topLeft + topRight + bottomLeft + bottomRight) / 4;
    const hasAlpha = cornerAvg <= 64;

    console.log(
      `[VP9 Alpha] sample ${vw}×${vh} | TL=${topLeft.toFixed(1)} TR=${topRight.toFixed(1)} BL=${bottomLeft.toFixed(1)} BR=${bottomRight.toFixed(1)} | avg=${cornerAvg.toFixed(1)} | ${hasAlpha ? 'NATIVE ALPHA' : 'NO ALPHA'}`,
    );

    return hasAlpha;
  } catch {
    return false;
  }
}

/**
 * Wait for the video to be ready, then detect native VP9 alpha.
 * Uses event listeners + polling as dual guarantee.
 * Calls `callback(hasNativeAlpha)` once detection completes.
 */
export function autoDetectAlpha(
  video: HTMLVideoElement,
  callback: (hasNativeAlpha: boolean) => void,
): void {
  let done = false;

  function runDetect() {
    if (done) return;
    done = true;

    clearInterval(pollId);
    events.forEach((ev) => video.removeEventListener(ev, onReady));

    const delay = video.videoWidth > 0 && video.videoHeight > 0 ? 200 : 800;
    setTimeout(() => {
      if (video.videoWidth === 0 || video.videoHeight === 0) {
        callback(false);
        return;
      }
      callback(detectNativeAlpha(video));
    }, delay);
  }

  const events = ['loadeddata', 'loadedmetadata', 'canplay', 'playing', 'timeupdate'];
  const onReady = () => runDetect();
  events.forEach((ev) => video.addEventListener(ev, onReady));

  let pollCount = 0;
  const pollId = setInterval(() => {
    pollCount++;
    if (video.readyState >= 2 && video.videoWidth > 0) {
      runDetect();
    }
    if (pollCount >= 80) {
      clearInterval(pollId);
      if (!done) {
        done = true;
        callback(false);
      }
    }
  }, 100);
}
